import { FilesetResolver, HandLandmarker, type HandLandmarkerResult } from '@mediapipe/tasks-vision'
import { type Landmark } from './PoseToRig'

/**
 * Hand-detection provider backed by `@mediapipe/tasks-vision`'s
 * `HandLandmarker`. Layered on top of the existing pose mocap pipeline:
 * `MocapController` runs both providers per frame and feeds each driver
 * (PoseToRig + HandToRig) independently so the body and finger bones get
 * driven without needing the much heavier Holistic model.
 *
 * Returns up to two hands per frame, each labelled `Left` or `Right` from
 * the user's perspective (MediaPipe corrects for the selfie-mirror so the
 * label matches the user's actual hand). World landmarks are in meters
 * relative to the wrist, with the same x-right/y-down/z-away convention as
 * `PoseLandmarker` — `media_to_three()` from PoseToRig converts cleanly.
 */

const WASM_BASE_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm'
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task'

export type Handedness = 'Left' | 'Right'

export interface DetectedHand {
  handedness: Handedness
  /** 21-element array of world landmarks (meters, BlazeHand taxonomy). */
  landmarks: Landmark[]
}

/**
 * Mirrors the `PoseProvider` interface in spirit but returns up to two
 * labelled hand landmark sets. Kept as a separate class (not a `PoseProvider`
 * implementation) because the return shape is fundamentally different —
 * arrays of arrays + handedness labels.
 */
export class MediaPipeHandProvider {
  private landmarker: HandLandmarker | null = null
  private last_video_time: number = -1

  async init (): Promise<void> {
    if (this.landmarker !== null) return // idempotent
    const vision = await FilesetResolver.forVisionTasks(WASM_BASE_URL)
    this.landmarker = await HandLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_URL,
        delegate: 'GPU'
      },
      runningMode: 'VIDEO',
      // Up to two hands at once; the body mocap doesn't care about more than
      // the user's two hands and bumping this past 2 just wastes inference.
      numHands: 2,
      minHandDetectionConfidence: 0.5,
      minHandPresenceConfidence: 0.5,
      minTrackingConfidence: 0.5
    })
  }

  async detect (video: HTMLVideoElement): Promise<DetectedHand[] | null> {
    if (this.landmarker === null) return null
    if (video.readyState < 2) return null

    // detectForVideo expects a strictly increasing timestamp. Skip duplicate
    // frames (when the video element hasn't advanced).
    const video_time = video.currentTime
    if (video_time === this.last_video_time) return null
    this.last_video_time = video_time

    try {
      const result: HandLandmarkerResult = this.landmarker.detectForVideo(video, performance.now())
      const world_lists = result.worldLandmarks ?? []
      const handednesses = result.handednesses ?? []
      if (world_lists.length === 0) return null

      const out: DetectedHand[] = []
      for (let i = 0; i < world_lists.length; i++) {
        const lms = world_lists[i] as Landmark[]
        const labels = handednesses[i]
        const top = labels?.[0]
        const handedness: Handedness = top?.categoryName === 'Right' ? 'Right' : 'Left'
        out.push({ handedness, landmarks: lms })
      }
      return out
    } catch (err) {
      console.warn('MediaPipeHandProvider.detect failed (will keep trying):', err)
      return null
    }
  }

  async dispose (): Promise<void> {
    if (this.landmarker === null) return
    try {
      await this.landmarker.close()
    } catch (_) { /* swallow — release is best-effort */ }
    this.landmarker = null
    this.last_video_time = -1
  }
}
